import { FiMapPin, FiAlertTriangle, FiNavigation, FiRefreshCw, FiShield } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { useAuthStore, useSettingsStore } from '../../store';
import './GeoFenceAlert.css';

const CONSOLTECH_WHATSAPP = '50368960231';

/** Distancia legible: 850 metros / 1.2 km */
const formatDistance = (meters) => {
  if (!Number.isFinite(meters)) return null;
  if (meters < 1000) return `${Math.round(meters)} metros`;
  return `${(meters / 1000).toFixed(1)} km`;
};

/**
 * Alerta de la geocerca. Tres variantes segun el veredicto del backend:
 *
 *  - `denied`      → el usuario negó el permiso: se le explica que es un requisito
 *                    de la licencia y se le ofrece reintentar.
 *  - `unavailable` → el equipo no pudo ubicarlo (sin GPS, sin señal): no es su culpa.
 *  - fuera del radio → uso fuera de la ubicación autorizada.
 *
 * `blocking` (modo block) no ofrece salida: no hay botón de continuar ni cierre
 * al hacer clic afuera. En los demás modos se puede seguir, y ese clic se registra.
 */
function GeoFenceAlert({ verdict, blocking = false, onContinue, onRetry }) {
  const { user } = useAuthStore();
  const { settings } = useSettingsStore();

  if (!verdict) return null;

  const shopName = verdict.shopName || settings?.name || 'su taller';
  const distance = formatDistance(verdict.distanceMeters);
  const isDenied = verdict.status === 'denied';
  const isUnavailable = verdict.status === 'unavailable';

  const whatsappMessage = encodeURIComponent(
    isDenied || isUnavailable
      ? `Hola ConsolTech, soy ${user?.name || ''} del taller ${shopName}. ` +
        `El sistema me pide la ubicacion y necesito ayuda para activarla.`
      : `Hola ConsolTech, soy ${user?.name || ''} del taller ${shopName}. ` +
        `Me aparece una alerta de uso fuera de la ubicacion autorizada` +
        `${distance ? ` (a ${distance} del punto registrado)` : ''}. ` +
        `Quisiera consultar sobre licencias para otras sucursales.`
  );
  const whatsappUrl = `https://wa.me/${CONSOLTECH_WHATSAPP}?text=${whatsappMessage}`;

  const renderIcon = () => {
    if (isDenied) return <FiShield />;
    if (isUnavailable) return <FiNavigation />;
    return blocking ? <FiAlertTriangle /> : <FiMapPin />;
  };

  return (
    <div className="overlay-geofencealert" role="dialog" aria-modal="true">
      <div className="modal-geofencealert">
        <div className={`icon-geofencealert ${blocking ? 'blocking-geofencealert' : ''}`}>
          {renderIcon()}
        </div>

        {isDenied ? (
          <>
            <h2 className="title-geofencealert">Se requiere el permiso de ubicación</h2>
            <p className="message-geofencealert">
              Las políticas de licencia de <strong>{shopName}</strong> requieren verificar que el
              sistema se esté usando en el taller autorizado. Para eso{' '}
              <strong>es indispensable permitir el acceso a la ubicación</strong> de este
              dispositivo.
            </p>
            <div className="steps-geofencealert">
              <span className="steps-title-geofencealert">Cómo activarlo</span>
              <ol>
                <li>Toque el ícono de candado (o de ubicación) en la barra de direcciones.</li>
                <li>Busque <strong>Ubicación</strong> y cámbielo a <strong>Permitir</strong>.</li>
                <li>Verifique que el GPS del dispositivo esté encendido.</li>
                <li>Vuelva a intentarlo con el botón de abajo.</li>
              </ol>
            </div>
          </>
        ) : isUnavailable ? (
          <>
            <h2 className="title-geofencealert">No pudimos obtener la ubicación</h2>
            <p className="message-geofencealert">
              El permiso está concedido, pero este dispositivo no logró determinar su ubicación.
            </p>
            <p className="hint-geofencealert">
              Verifique que el GPS esté encendido y que tenga señal. Si está en un sótano o en un
              local cerrado, pruebe cerca de una ventana.
            </p>
          </>
        ) : (
          <>
            <h2 className="title-geofencealert">Uso fuera de la ubicación autorizada</h2>
            <p className="message-geofencealert">
              Este sistema está licenciado para operar en <strong>{shopName}</strong>.
              {distance
                ? <> Se detectó un acceso a <strong>{distance}</strong> del punto registrado.</>
                : ' Se detectó un acceso fuera del área registrada.'}
            </p>
            <p className="hint-geofencealert">
              Si su taller cambió de dirección o necesita licencias para otras sucursales,
              escríbanos y lo resolvemos.
            </p>
          </>
        )}

        <div className="actions-geofencealert">
          {(isDenied || isUnavailable) && onRetry && (
            <button className="button-retry-geofencealert" onClick={onRetry}>
              <FiRefreshCw /> Reintentar
            </button>
          )}

          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="button-whatsapp-geofencealert"
          >
            <FaWhatsapp /> Contactar a ConsolTech
          </a>

          {!blocking && (
            <button className="button-continue-geofencealert" onClick={onContinue}>
              {isDenied || isUnavailable ? 'Continuar sin ubicación' : 'Continuar de todos modos'}
            </button>
          )}
        </div>

        {blocking && (
          <p className="footnote-geofencealert">
            El acceso está restringido hasta resolver esta situación con ConsolTech.
          </p>
        )}
      </div>
    </div>
  );
}

export default GeoFenceAlert;
